import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Sparkles, Loader2 } from "lucide-react";
import { api } from "../../api/client";

/**
 * Stripe-backed pricing modal.
 *
 * Backend is the single source of truth for trial eligibility and pricing.
 * Frontend posts {payment_type, billing_period, offer_type, origin_url} and
 * gets back a Stripe Checkout URL — we then redirect the browser there.
 *
 * Downsell flow lives on the Landing page (see Landing.jsx). When the user
 * cancels Stripe Checkout, Stripe redirects to `/?stripe_canceled=1&offer=…`
 * and Landing shows the discounted-trial modal after a 2 s grace period.
 */

const PLAN_DISPLAY = {
  monthly: {
    label: "Monthly",
    regular: "$39.99",
    period: "PER MONTH",
    originalPrice: null,
    fullTrial: "$3.99",
    discountedTrial: "$1.99",
  },
  yearly: {
    label: "Yearly",
    regular: "$383.99",
    period: "PER YEAR",
    originalPrice: "$479.88",
    fullTrial: "$3.99",
    discountedTrial: "$1.99",
  },
};

const FEATURES = [
  { bold: "100 Credits", text: "per month for all generations" },
  { bold: "Full Song", text: "generation with AI vocals" },
  { bold: "Video", text: "generation for your tracks" },
  { bold: "AI Covers", text: "with custom voice models" },
  { bold: "Duets & Karaoke", text: "modes included" },
];

const CREDIT_OPTIONS = [
  { key: "50", credits: 50, price: "$19.99", label: "Buy 50 Credits" },
  { key: "125", credits: 125, price: "$44.99", label: "Buy 125 Credits" },
  { key: "400", credits: 400, price: "$89.99", label: "Buy 400 Credits", badge: "Save 20%" },
];

export default function PricingModal({ open, onClose, user, onAfterUpdate }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [eligibility, setEligibility] = useState({ trial_eligible: false, has_subscription: false });
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [selectedCredits, setSelectedCredits] = useState("50");
  const [checkoutError, setCheckoutError] = useState(null);

  const isAuthenticated = !!user?.id;

  // Load Stripe config + eligibility on open
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.get("/stripe/config");
        if (!cancelled) setStripeEnabled(!!cfg.data?.enabled);
      } catch {
        if (!cancelled) setStripeEnabled(false);
      }
      if (isAuthenticated) {
        try {
          const elig = await api.get("/stripe/eligibility");
          if (!cancelled) setEligibility(elig.data || {});
        } catch {
          if (!cancelled) setEligibility({ trial_eligible: false, has_subscription: false });
        }
      }
    })();
    setSelectedPlan("monthly");
    setSelectedCredits("50");
    setCheckoutError(null);
    return () => { cancelled = true; };
  }, [open, isAuthenticated]);

  const hasActiveSubscription = !!eligibility.has_subscription || !!user?.subscription_active;
  const isOnTrial = !!user?.is_trial;
  const currentPlan = user?.plan || null;
  const canStartTrial = isAuthenticated && eligibility.trial_eligible && !hasActiveSubscription;

  async function startCheckout({ payment_type, billing_period, offer_type, credits_amount }) {
    if (!isAuthenticated) {
      onClose();
      navigate("/", { state: { showAuth: true } });
      return;
    }
    if (!stripeEnabled) {
      setCheckoutError({
        title: "Payments are temporarily unavailable",
        body: "Our payment processor isn't configured. Please try again shortly — no charge was made.",
      });
      return;
    }
    setCheckoutError(null);
    setBusy(true);
    try {
      const body = { payment_type, origin_url: window.location.origin };
      if (payment_type === "subscription") {
        body.billing_period = billing_period;
        body.offer_type = offer_type;
      } else if (payment_type === "credits") {
        body.credits_amount = credits_amount;
      }
      const res = await api.post("/stripe/checkout", body);
      const url = res?.data?.url;
      if (!url) {
        throw new Error("No checkout URL");
      }
      // Stash a "pending downsell" hint in sessionStorage BEFORE redirecting
      // to Stripe. Stripe's cancel_url only fires if the user clicks
      // Cancel/X in the Checkout UI — clicking the browser Back button
      // skips it entirely. By writing here, the Landing page can detect
      // "user came back without paying" via sessionStorage on mount and
      // surface the discounted-trial modal anyway.
      try {
        if (payment_type === "subscription" && offer_type === "full_trial") {
          window.sessionStorage.setItem("stripe_pending_downsell", JSON.stringify({
            offer: "full_trial",
            plan: billing_period,
            session_id: res.data.session_id,
            at: Date.now(),
          }));
        } else {
          // Any other checkout intent → clear any stale downsell hint so
          // the user doesn't get a stale modal a week later.
          window.sessionStorage.removeItem("stripe_pending_downsell");
        }
      } catch (_) { /* ignore quota / privacy mode errors */ }
      // Full-page redirect to Stripe-hosted Checkout
      window.location.href = url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || String(e?.message || e);
      let title = "Couldn't start checkout";
      let body = "Something interrupted the payment flow. Please try again in a moment — no charge was made.";
      if (status === 403) {
        title = "Trial not available";
        body = "This trial offer can only be used once. Please pick a regular plan to continue.";
      }
      setCheckoutError({ title, body: body + " (" + detail + ")" });
      setBusy(false);
    }
  }

  function handleStartTrial() {
    return startCheckout({
      payment_type: "subscription",
      billing_period: selectedPlan,
      offer_type: "full_trial",
    });
  }

  function handleSubscribe() {
    return startCheckout({
      payment_type: "subscription",
      billing_period: selectedPlan,
      offer_type: "regular",
    });
  }

  function handleBuyCredits() {
    const opt = CREDIT_OPTIONS.find((o) => o.key === selectedCredits) || CREDIT_OPTIONS[0];
    return startCheckout({
      payment_type: "credits",
      credits_amount: opt.credits,
    });
  }

  async function cancelSubscription() {
    if (!window.confirm("Are you sure you want to cancel your subscription? Your access will continue until the end of the billing period.")) {
      return;
    }
    try {
      setBusy(true);
      const r = await api.post("/stripe/cancel-subscription", {});
      if (onAfterUpdate) onAfterUpdate();
      window.alert(r.data?.message || "Subscription canceled.");
    } catch (e) {
      window.alert("Cancel failed: " + (e?.response?.data?.detail || String(e)));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  // Helper used by both close paths (X button + backdrop click). When the
  // user dismisses the pricing modal without checking out AND is still
  // eligible for a discounted trial, dispatch a window event that the
  // app-root <GlobalDownsellHost /> listens for. That host then surfaces
  // the discounted-$1.99 last-chance modal.
  //
  // Eligibility for the modal:
  //   • anonymous user (might still convert via signup) → SHOW
  //   • authenticated, no subscription, hasn't used trial → SHOW
  //   • authenticated with active subscription → don't show (no nag)
  //   • authenticated, already used trial → don't show (backend would 403 anyway)
  const eligibleForDownsell =
    !hasActiveSubscription &&
    (!isAuthenticated || eligibility.trial_eligible);

  function handleClose() {
    try {
      if (eligibleForDownsell) {
        window.dispatchEvent(new CustomEvent("stripe:open-downsell", {
          detail: { plan: selectedPlan },
        }));
      }
    } catch (_) { /* ignore — non-critical */ }
    onClose?.();
  }

  const planData = PLAN_DISPLAY[selectedPlan];
  const trialPriceLabel = planData.fullTrial;
  const isCurrentPlanActive = hasActiveSubscription && currentPlan === selectedPlan && !isOnTrial;

  return (
    <div className="pricing-overlay" onClick={handleClose}>
      <div className="pricing-modal-v2" onClick={(e) => e.stopPropagation()} data-testid="pricing-modal">
        <button className="pricing-close-btn" onClick={handleClose} data-testid="pricing-close-btn">
          <X size={20} />
        </button>

        <h1 className="pricing-title">PRICING</h1>

        <div className={`pricing-toggle ${selectedPlan === "yearly" ? "yearly-active" : ""}`}>
          <button
            className={`pricing-toggle-btn ${selectedPlan === "monthly" ? "" : "inactive"}`}
            onClick={() => setSelectedPlan("monthly")}
            data-testid="pricing-toggle-monthly"
          >
            Monthly
          </button>
          <button
            className={`pricing-toggle-btn ${selectedPlan === "yearly" ? "" : "inactive"}`}
            onClick={() => setSelectedPlan("yearly")}
            data-testid="pricing-toggle-yearly"
          >
            Yearly
          </button>
        </div>

        {checkoutError && (
          <div className="pricing-checkout-error" role="alert" data-testid="pricing-checkout-error">
            <div className="pricing-checkout-error-title">{checkoutError.title}</div>
            <div className="pricing-checkout-error-body">{checkoutError.body}</div>
            <button
              className="pricing-checkout-error-close"
              onClick={() => setCheckoutError(null)}
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="pricing-card">
          <h2 className="pricing-card-title">Features</h2>
          <p className="pricing-card-subtitle">
            Unlock all premium features and create unlimited music
          </p>

          <div className="pricing-price-row" key={selectedPlan}>
            {planData.originalPrice && (
              <span className="pricing-original-price">{planData.originalPrice}</span>
            )}
            <span className="pricing-current-price">{planData.regular}</span>
            <span className="pricing-period">{planData.period}</span>
          </div>

          <ul className="pricing-features-list">
            {FEATURES.map((feature, idx) => (
              <li key={idx}>
                <Check className="pricing-check-icon" size={18} />
                <span><strong>{feature.bold}</strong> {feature.text}</span>
              </li>
            ))}
          </ul>

          {canStartTrial && (
            <div
              onClick={!busy ? handleStartTrial : undefined}
              data-testid="start-trial-btn"
              className="pricing-trial-btn"
              style={{
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? (
                <span className="pricing-busy"><Loader2 size={16} className="pricing-spin" /> Processing…</span>
              ) : (
                <>Start 3-Day Trial for {trialPriceLabel}</>
              )}
            </div>
          )}

          <button
            className={`pricing-subscribe-btn ${isCurrentPlanActive ? "disabled" : ""}`}
            onClick={handleSubscribe}
            disabled={busy || isCurrentPlanActive}
            data-testid="subscribe-btn"
          >
            {busy ? (
              <span className="pricing-busy"><Loader2 size={16} className="pricing-spin" /> Processing…</span>
            ) : isCurrentPlanActive ? "Already Subscribed" : "Subscribe"}
          </button>
        </div>

        {isAuthenticated && hasActiveSubscription && (
          <div className="pricing-card pricing-credits-card">
            <h2 className="pricing-card-title">Buy Credits</h2>
            <p className="pricing-card-subtitle">
              If you need more credits, you can buy them at a great price.
            </p>

            <div className="pricing-credit-options">
              {CREDIT_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  className={`pricing-credit-option ${selectedCredits === option.key ? "selected" : ""}`}
                  onClick={() => setSelectedCredits(option.key)}
                  disabled={busy}
                  data-testid={`pricing-credit-${option.key}`}
                >
                  <span className="pricing-radio">
                    <span className="pricing-radio-inner"></span>
                  </span>
                  <span className="pricing-credit-label">{option.label}</span>
                  {option.badge && (
                    <span className="pricing-credit-badge">{option.badge}</span>
                  )}
                  <span className="pricing-credit-price">{option.price}</span>
                </button>
              ))}
            </div>

            <button
              className="pricing-subscribe-btn"
              onClick={handleBuyCredits}
              disabled={busy}
              data-testid="modal-buy-credits-btn"
            >
              {busy ? (
                <span className="pricing-busy"><Loader2 size={16} className="pricing-spin" /> Processing…</span>
              ) : "Buy Credits"}
            </button>
          </div>
        )}

        {hasActiveSubscription && !isOnTrial && (
          <button
            className="pricing-cancel-link"
            onClick={cancelSubscription}
            disabled={busy}
            data-testid="cancel-subscription-btn"
          >
            Cancel Subscription
          </button>
        )}

        <div className="pricing-secure-note">
          <Sparkles size={12} /> Secured by Stripe — your card is never stored on our servers.
        </div>
      </div>
    </div>
  );
}
